import type { RibbonState } from '../state/selectors';
import { teamColor } from '../state/teamColors';
import styles from './Ribbon.module.css';

interface RibbonProps {
  state: RibbonState;
}

function Dots({
  count,
  total,
  label,
  filledClass,
  emptyClass,
  testId,
}: {
  count: number;
  total: number;
  label: string;
  filledClass: string;
  emptyClass: string;
  testId: string;
}): JSX.Element {
  return (
    <div className={styles.countGroup} data-testid={testId}>
      <span className={styles.countLabel}>{label}</span>
      <span className={styles.dots} aria-label={`${count} ${label.toLowerCase()}`}>
        {Array.from({ length: total }).map((_, i) => (
          <span
            key={i}
            className={`${styles.dot} ${i < count ? filledClass : emptyClass}`}
            aria-hidden="true"
          />
        ))}
      </span>
    </div>
  );
}

export default function Ribbon({ state }: RibbonProps): JSX.Element {
  const away = teamColor(state.awayTeamCode);
  const home = teamColor(state.homeTeamCode);
  return (
    <header data-testid="ribbon" className={styles.ribbon}>
      <div className={styles.scoreGroup}>
        <span
          className={styles.teamChip}
          style={{ background: away.primary }}
          data-testid="ribbon-away"
        >
          <span className={styles.teamAbbr}>{state.awayAbbr}</span>
          <span className={styles.teamScore}>{state.awayScore}</span>
        </span>
        <span className={styles.at}>@</span>
        <span
          className={styles.teamChip}
          style={{ background: home.primary }}
          data-testid="ribbon-home"
        >
          <span className={styles.teamAbbr}>{state.homeAbbr}</span>
          <span className={styles.teamScore}>{state.homeScore}</span>
        </span>
      </div>
      <div data-testid="ribbon-inning" className={styles.inning}>
        {state.inningOrdinal != null
          ? `${state.inningHalf ?? ''} ${state.inningOrdinal}`.trim()
          : state.inning != null
            ? `Inning ${state.inning}`
            : ''}
      </div>
      <div className={styles.count} data-testid="ribbon-count">
        <Dots
          count={state.balls}
          total={3}
          label="B"
          filledClass={`${styles.dotBall} ${styles.dotBallFilled}`}
          emptyClass={styles.dotBall ?? ''}
          testId="dots-balls"
        />
        <Dots
          count={state.strikes}
          total={2}
          label="S"
          filledClass={`${styles.dotStrike} ${styles.dotStrikeFilled}`}
          emptyClass={styles.dotStrike ?? ''}
          testId="dots-strikes"
        />
        <Dots
          count={state.outs}
          total={2}
          label="O"
          filledClass={`${styles.dotOut} ${styles.dotOutFilled}`}
          emptyClass={styles.dotOut ?? ''}
          testId="dots-outs"
        />
      </div>
    </header>
  );
}
